// Helpers for the "Custom HTML/CSS" slide block.
//
// The block hands us two untrusted strings — a chunk of markup and a chunk of
// CSS — and both have to land in the app's own DOM. The markup half goes
// through `sanitizeSlideLayoutHtml` (src/utils/sanitizeHtml.ts). This module is
// the CSS half:
//
//   * pull <style> out of the pasted markup so the rules apply instead of
//     showing up as text on the slide,
//   * strip the few CSS constructs that are script/exfiltration vectors rather
//     than styling,
//   * prefix every selector so a slide's rules cannot repaint the rest of the
//     deck — unless the author explicitly asked for deck scope.

const STYLE_TAG = /<style\b[^>]*>([\s\S]*?)<\/style\s*>/gi;

const CSS_COMMENT = /\/\*[\s\S]*?\*\//g;

/**
 * CSS constructs that are not styling.
 *
 * `@import` and `url(http…)` inside a stylesheet fetch from a third party,
 * which turns a shared deck into a beacon. `expression()`, `-moz-binding` and
 * `behavior:` are the legacy script-in-CSS sinks. A stray `</style` would end
 * our own style element early and let the rest of the string be parsed as
 * markup.
 */
const CSS_STRIP_PATTERNS = [
  [/@import\b[^;]*;?/gi, ''],
  [/expression\s*\(/gi, 'expression_('],
  [/-moz-binding\s*:/gi, '-moz-binding-blocked:'],
  [/\bbehavior\s*:/gi, 'behavior-blocked:'],
  [/javascript\s*:/gi, 'javascript-blocked:'],
  [/<\s*\/\s*style/gi, '&lt;/style'],
];

/**
 * The same list in words, for the brief the block's editor hands to an LLM.
 * Kept beside the patterns so the two cannot describe different rules.
 */
export const CSS_STRIP_RULES = [
  '`@import` — deleted. Nothing may be fetched from another origin; embed what you need.',
  '`expression(...)` — defused (legacy IE script-in-CSS).',
  '`-moz-binding:` and `behavior:` — defused (legacy script-in-CSS).',
  '`javascript:` in any value — defused.',
  '`</style` — escaped, so a stray one cannot end the stylesheet early.',
];

/**
 * Split `<style>` blocks out of a markup string.
 *
 * Returns the markup with the style elements removed and their contents joined
 * into one CSS string, so both halves can be handled by the code that knows
 * what to do with them.
 */
export const extractStyleBlocks = (html = '') => {
  const source = typeof html === 'string' ? html : '';
  const css = [];
  const markup = source.replace(STYLE_TAG, (_match, body) => {
    if (body && body.trim()) css.push(body.trim());
    return '';
  });
  return { html: markup, css: css.join('\n\n') };
};

/** Remove the non-styling constructs listed above. */
export const sanitizeCustomCss = (css = '') => {
  const source = typeof css === 'string' ? css : '';
  if (!source.trim()) return '';
  return CSS_STRIP_PATTERNS
    .reduce((acc, [pattern, replacement]) => acc.replace(pattern, replacement), source)
    .trim();
};

/**
 * Prefix every selector in `css` with `scopeSelector`.
 *
 * `html`, `body` and `:root` collapse onto the scope element itself, so an
 * author who writes `body { background: navy }` gets the slide painted rather
 * than nothing at all. At-rules that wrap other rules (@media, @supports,
 * @layer, @container) are recursed into; @font-face and @keyframes are copied
 * through untouched, because scoping their bodies would break them.
 */
export const scopeCss = (css, scopeSelector) => {
  const source = typeof css === 'string' ? css : '';
  if (!source.trim() || !scopeSelector) return source.trim();

  const prefixSelectors = (selectorList) =>
    selectorList
      // A comment sitting inside a selector list is not part of any selector.
      .replace(CSS_COMMENT, ' ')
      .split(',')
      .map((selector) => selector.trim())
      .filter(Boolean)
      .map((selector) => {
        if (selector === 'html' || selector === 'body') return scopeSelector;
        if (selector.startsWith(':root')) return scopeSelector + selector.slice(5);
        return `${scopeSelector} ${selector}`;
      })
      .join(', ');

  let out = '';
  let i = 0;
  while (i < source.length) {
    // Comments first. Without this the parser reads `/* a note, or two */` as a
    // selector list, prefixes each comma-separated fragment, and then hands the
    // NEXT rule's selector slot to the comment — leaving that rule unscoped and
    // free to style the rest of the deck. Commented CSS is the normal kind, so
    // this path matters more than it looks.
    if (source.startsWith('/*', i)) {
      const commentEnd = source.indexOf('*/', i + 2);
      if (commentEnd === -1) { out += source.slice(i); break; }
      out += source.slice(i, commentEnd + 2);
      i = commentEnd + 2;
      continue;
    }

    // Copied verbatim: scoping the inside of these would break them.
    if (/^@(?:font-face|keyframes|-webkit-keyframes|charset|namespace)\b/i.test(source.slice(i))) {
      const end = source.indexOf('}', i);
      if (end === -1) { out += source.slice(i); break; }
      out += source.slice(i, end + 1);
      i = end + 1;
      continue;
    }

    // Conditional groups: keep the head, scope the rules inside.
    const nested = source.slice(i).match(/^@(?:media|supports|layer|container|scope)[^{]*\{/i);
    if (nested) {
      const head = nested[0];
      let j = i + head.length;
      let depth = 1;
      while (j < source.length && depth) {
        if (source[j] === '{') depth++;
        else if (source[j] === '}') depth--;
        j++;
      }
      out += head + scopeCss(source.slice(i + head.length, j - 1), scopeSelector) + '}';
      i = j;
      continue;
    }

    const selectorEnd = source.indexOf('{', i);
    if (selectorEnd === -1) { out += source.slice(i); break; }
    const bodyEnd = source.indexOf('}', selectorEnd);
    if (bodyEnd === -1) { out += source.slice(i); break; }

    const selector = source.slice(i, selectorEnd).trim();
    const body = source.slice(selectorEnd, bodyEnd + 1);
    out += `${prefixSelectors(selector)} ${body}`;
    i = bodyEnd + 1;
  }

  return out.trim();
};

/** Quote-safe attribute selector for the block's own wrapper. */
export const customHtmlScopeSelector = (blockId) =>
  `.slide-custom-html[data-block="${String(blockId || '').replace(/["\\]/g, '')}"]`;

/** True when the block's CSS is meant to reach the whole deck. */
export const isDeckScopedCustomHtml = (block = {}) => block.cssScope === 'deck';

/**
 * The slice of the editor's CSS reset that changes how a layout looks.
 *
 * The app page runs Tailwind's preflight; the exported HTML file and a rendered
 * SVG do not. Anything a layout leaves to a browser default therefore renders
 * one way in the editor and another everywhere else — headings come back
 * browser-bold and oversized, lists sprout markers, paragraphs gain margins.
 * Shipping the reset with the block is what makes the three agree.
 *
 * Scoped to the block by its callers, never applied to a whole document.
 */
export const EDITOR_RESET_CSS = `
*, *::before, *::after { box-sizing: border-box; }
h1, h2, h3, h4, h5, h6 { font-size: inherit; font-weight: inherit; margin: 0; }
p, figure, blockquote, dl, dd { margin: 0; }
ol, ul { list-style: none; margin: 0; padding: 0; }
img, svg, video, canvas { display: block; max-width: 100%; }
table { border-collapse: collapse; }
`;

/**
 * How a linked clip looks before the author styles it.
 *
 * An author writes `<a data-lp-video="video-1"></a>` and gets a poster wrapped
 * in a link; with no default styling that is a bare thumbnail with nothing to
 * say it is playable. These rules make it read as a video card — 16:9, rounded,
 * with a play glyph — and every one of them is a plain class selector the
 * author's own CSS overrides, because theirs is emitted afterwards.
 *
 * Sized in `cqw` so the card scales with the slide, the drawer thumbnail and
 * the rasterized export alike.
 */
export const VIDEO_BASE_CSS = `
.lp-video {
  position: relative;
  display: block;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  border-radius: 0.8cqw;
  background: #0f172a;
  color: #f8fafc;
  text-decoration: none;
}
.lp-video__poster { width: 100%; height: 100%; object-fit: cover; }
.lp-video::after {
  content: "";
  position: absolute;
  inset: 0;
  margin: auto;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 1.6cqw 0 1.6cqw 2.7cqw;
  border-color: transparent transparent transparent rgba(255, 255, 255, 0.94);
  filter: drop-shadow(0 0.2cqw 0.6cqw rgba(2, 6, 23, 0.55));
}
.lp-video--text {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2cqw 2cqw 2cqw 6cqw;
  font-size: 1.7cqw;
  line-height: 1.35;
  text-align: left;
  overflow-wrap: anywhere;
}
.lp-video--text::after { inset: 0 auto 0 2.4cqw; }
`;

/**
 * The attribute an author's markup uses to point at an uploaded picture:
 * `<img data-lp-image="image-1" alt="…">`.
 *
 * Uploads are kept on the block, next to the markup rather than inside it, so
 * the HTML an assistant writes stays short and readable and the picture is a
 * real asset the PowerPoint and PDF exporters can place. The reference is
 * resolved to a genuine `src` on the way to every renderer.
 */
export const IMAGE_REF_ATTR = 'data-lp-image';

/** The CSS custom property that carries an upload, for use as a background. */
export const imageCssVarName = (name) =>
  `--lp-${String(name || '').trim().replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'image'}`;

const normalizeImages = (images) =>
  (Array.isArray(images) ? images : [])
    .filter((image) => image && typeof image === 'object' && typeof image.src === 'string' && image.src)
    .map((image, index) => ({
      ...image,
      name: String(image.name || `image-${index + 1}`).trim() || `image-${index + 1}`,
    }));

/**
 * Swap every `data-lp-image` reference for the upload it names.
 *
 * Runs before sanitizing on purpose: the `src` we inject is then checked by the
 * same allowlist as any other image, so a stored value that is not a real
 * raster data URL is dropped rather than trusted because we put it there.
 *
 * A reference with no matching upload keeps its attribute and gains no src —
 * it renders as an empty image rather than silently vanishing, which is the
 * honest signal that the upload was removed or the name was mistyped.
 */
export const applyCustomHtmlImages = (html = '', images = []) => {
  const markup = typeof html === 'string' ? html : '';
  const pool = normalizeImages(images);
  if (!markup || !pool.length) return markup;
  if (typeof DOMParser === 'undefined') return markup;
  if (!markup.toLowerCase().includes(IMAGE_REF_ATTR)) return markup;

  const byName = new Map(pool.map((image) => [image.name.toLowerCase(), image]));
  const doc = new DOMParser().parseFromString(`<body>${markup}</body>`, 'text/html');

  doc.body.querySelectorAll(`[${IMAGE_REF_ATTR}]`).forEach((el) => {
    const image = byName.get((el.getAttribute(IMAGE_REF_ATTR) || '').trim().toLowerCase());
    if (!image) return;
    el.setAttribute('src', image.src);
    if (!el.getAttribute('alt') && image.alt) el.setAttribute('alt', image.alt);
    // Intrinsic size travels with the picture so the layout does not reflow
    // once it loads, and so the exporters know its aspect ratio.
    if (image.width && !el.getAttribute('width')) el.setAttribute('width', String(image.width));
    if (image.height && !el.getAttribute('height')) el.setAttribute('height', String(image.height));
  });

  return doc.body.innerHTML;
};

/**
 * The attribute an author's markup uses to point at a linked video:
 * `<a data-lp-video="video-1"></a>` or `<div data-lp-video="video-1"></div>`.
 *
 * A video becomes a poster image wrapped in a link, not an embedded player.
 * The slide-layout sanitizer forbids <iframe> — a hand-written layout renders
 * in the app's own DOM, and a frame there is a script vector, not a picture —
 * and a poster is the only form that survives every destination anyway: it
 * shows in the editor, in the exported HTML, in the rasterized PowerPoint slide
 * and in the PDF, and stays clickable wherever links work.
 */
export const VIDEO_REF_ATTR = 'data-lp-video';

/** Watch and thumbnail URLs for the platforms the app already understands. */
export const videoLinkInfo = (video = {}) => {
  const url = String(video.url || '').trim();
  const platform = String(video.platform || '').trim();
  const videoId = String(video.videoId || '').trim();
  if (platform === 'youtube' && videoId) {
    return {
      watchUrl: url || `https://www.youtube.com/watch?v=${videoId}`,
      posterUrl: video.poster || `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
    };
  }
  if (platform === 'vimeo' && videoId) {
    return { watchUrl: url || `https://vimeo.com/${videoId}`, posterUrl: video.poster || '' };
  }
  return { watchUrl: url, posterUrl: video.poster || '' };
};

const normalizeVideos = (videos) =>
  (Array.isArray(videos) ? videos : [])
    .filter((video) => video && typeof video === 'object' && String(video.url || '').trim())
    .map((video, index) => ({
      ...video,
      name: String(video.name || `video-${index + 1}`).trim() || `video-${index + 1}`,
    }));

/**
 * Swap every `data-lp-video` reference for a poster-and-link figure.
 *
 * The referenced element is replaced wholesale rather than annotated: an author
 * writes an empty placeholder and gets the real thing, so their markup stays
 * free of platform URLs and thumbnail paths.
 */
export const applyCustomHtmlVideos = (html = '', videos = []) => {
  const markup = typeof html === 'string' ? html : '';
  const pool = normalizeVideos(videos);
  if (!markup || !pool.length) return markup;
  if (typeof DOMParser === 'undefined') return markup;
  if (!markup.toLowerCase().includes(VIDEO_REF_ATTR)) return markup;

  const byName = new Map(pool.map((video) => [video.name.toLowerCase(), video]));
  const doc = new DOMParser().parseFromString(`<body>${markup}</body>`, 'text/html');

  doc.body.querySelectorAll(`[${VIDEO_REF_ATTR}]`).forEach((el) => {
    const video = byName.get((el.getAttribute(VIDEO_REF_ATTR) || '').trim().toLowerCase());
    if (!video) return;
    const { watchUrl, posterUrl } = videoLinkInfo(video);
    if (!watchUrl) return;

    const link = doc.createElement('a');
    link.setAttribute('href', watchUrl);
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
    link.setAttribute('class', `lp-video ${el.getAttribute('class') || ''}`.trim());
    link.setAttribute(VIDEO_REF_ATTR, video.name);
    if (video.title) link.setAttribute('title', video.title);

    if (posterUrl) {
      const poster = doc.createElement('img');
      poster.setAttribute('src', posterUrl);
      poster.setAttribute('alt', video.title || 'Video thumbnail');
      poster.setAttribute('class', 'lp-video__poster');
      link.appendChild(poster);
    } else {
      // No thumbnail to show, so the card carries the clip's name instead. The
      // modifier is what the stylesheet keys off — it cannot ask "does this
      // link contain an image?" in a way every destination understands.
      link.setAttribute('class', `${link.getAttribute('class')} lp-video--text`.trim());
      link.textContent = video.title || watchUrl;
    }
    el.replaceWith(link);
  });

  return doc.body.innerHTML;
};

/**
 * Expose the uploads to the CSS field as custom properties, so a layout can use
 * one as a backdrop (`background-image: var(--lp-image-1)`) and not only as an
 * `<img>`. Always scoped to the block itself, deck-scoped CSS included — the
 * variables belong to this block's pictures wherever its rules end up.
 */
export const buildCustomHtmlImageVars = (block = {}) => {
  const pool = normalizeImages(block.images);
  if (!pool.length) return '';
  const declarations = pool
    .map((image) => `  ${imageCssVarName(image.name)}: url("${image.src.replace(/["\\]/g, '')}");`)
    .join('\n');
  return `${customHtmlScopeSelector(block.id)} {\n${declarations}\n}`;
};

/**
 * Everything the renderers need from one custom-html block: sanitized markup,
 * and the CSS already scoped (or deliberately left global).
 *
 * `<style>` found inside the markup is folded in ahead of the block's own CSS
 * field, so a pasted page and a hand-written rule end up in the same cascade
 * order the author saw when they wrote them.
 *
 * `imageVarsCss` is returned separately from `css` because it is scoped to the
 * block even when the author's own rules are deliberately global: the pictures
 * belong to this block wherever its styling ends up.
 */
export const prepareCustomHtmlBlock = (block = {}) => {
  const { html: markup, css: inlineCss } = extractStyleBlocks(block.html || '');
  const combined = [inlineCss, block.css || '']
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join('\n\n');

  const cleaned = sanitizeCustomCss(combined);
  const deckScoped = isDeckScopedCustomHtml(block);
  const scopeSelector = customHtmlScopeSelector(block.id);

  return {
    html: applyCustomHtmlVideos(applyCustomHtmlImages(markup, block.images), block.videos),
    css: deckScoped ? cleaned : scopeCss(cleaned, scopeSelector),
    imageVarsCss: buildCustomHtmlImageVars(block),
    // Emitted ahead of the author's rules so theirs win, and scoped to the
    // block so it cannot reset the rest of an exported page.
    //
    // The two are returned separately because they are needed in different
    // places: `resetCss` only matters where Tailwind's preflight is absent (the
    // exported file, the rasterized slide), while `baseCss` — the video card —
    // has to ship everywhere, the live editor included.
    resetCss: scopeCss(EDITOR_RESET_CSS, scopeSelector),
    baseCss: scopeCss(VIDEO_BASE_CSS, scopeSelector),
    deckScoped,
    scopeSelector,
  };
};
